import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

type Me = { discordId:string; tag?:string|null; isAdmin:boolean } | null
type ManualApproval = { by:string; tag?:string|null; at:string }
type Answer = {
  id: string
  given?: string
  correct?: boolean
  timeMs?: number
  pasted?: boolean
  manual?: ManualApproval | null
  effectiveCorrect?: boolean
}
type Result = { good:number; total:number; percent:number; pass:boolean; avgMs?:number; approvedCount?:number }
type SummaryData = {
  exam: string
  discordId: string
  candidateNick?: string | null
  candidateTag?: string | null
  channelId: string
  examinerId?: string | null
  examinerNick?: string | null
  extension?: string
  startedAt?: string
  createdAt: string
  finishedAt?: string | null
  result: Result
  answers: Answer[]
}

export default function SummaryPage() {
  const { sessionId } = useParams()

  // --- STANY / HOOKI (muszą być zawsze wywołane, przed ewentualnym return) ---
  const [data, setData] = useState<SummaryData | null>(null)
  const [msg, setMsg] = useState('')

  const [sortMode, setSortMode] = useState<'default'|'wrongFirst'>('default')
  const [me, setMe] = useState<Me>(null)
  const [audit, setAudit] = useState<any[]>([])
  const [loadingAudit, setLoadingAudit] = useState(false)
  const [pending, setPending] = useState<Record<string, boolean>>({})
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)

  // kto zalogowany
  useEffect(()=>{
    fetch('/api/me').then(r=>r.json()).then(d=>setMe(d.me || null)).catch(()=>setMe(null))
  },[])

  // wczytaj wynik
  useEffect(()=>{
    (async()=>{
      try{
        const r = await fetch(`/api/result/${sessionId}`)
        if (!r.ok) throw new Error('Błąd wczytywania')
        const d = await r.json()
        const answers: Answer[] = (d.answers || []).map((a:Answer)=>({
          ...a,
          effectiveCorrect: typeof a.effectiveCorrect === 'boolean'
            ? a.effectiveCorrect
            : !!a.correct || !!(a.manual && (a.manual as any).by)
        }))
        setData({ ...d, answers })
      }catch(e:any){
        setMsg(e.message || String(e))
      }
    })()
  }, [sessionId])

  const canModerate = !!me && !!data && (me.isAdmin || (data.examinerId && String(me.discordId) === String(data.examinerId)))

  async function loadAudit(){
    if (!canModerate || !sessionId) return
    setLoadingAudit(true)
    try{
      const r = await fetch(`/api/admin/audit/${sessionId}`)
      const d = await r.json()
      if (r.ok && d.ok) setAudit(d.audit || [])
    } finally {
      setLoadingAudit(false)
    }
  }
  useEffect(()=>{ loadAudit() }, [canModerate, sessionId])

  async function toggleApprove(answerId:string, approve:boolean) {
    if (!sessionId) return
    setPending(p => ({...p, [answerId]: true}))
    try {
      const r = await fetch('/api/admin/approve-answer', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ sessionId, answerId, approve })
      })
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || 'Błąd')

      const updatedAnswers: Answer[] = (d.answers || []).map((a:Answer)=>({
        ...a,
        effectiveCorrect: typeof a.effectiveCorrect === 'boolean'
          ? a.effectiveCorrect
          : !!a.correct || !!(a.manual && (a.manual as any).by)
      }))
      setData(prev => prev ? ({ ...prev, result: d.result, answers: updatedAnswers }) : prev)
      loadAudit()
    } catch(e:any) {
      alert(e.message || String(e))
    } finally {
      setPending(p => { const np = {...p}; delete np[answerId]; return np })
    }
  }

  // wartości zależne – bezpieczne, gdy data jeszcze null
  const avgSec = data?.result?.avgMs ? Math.round(data!.result.avgMs/1000) : null
  const pass = !!data?.result?.pass
  const ext = data?.extension || 'jpg'

  // sortowana lista do wyświetlenia (działa też, gdy data=null -> pusta lista)
  const displayed = useMemo(()=>{
    const base: Answer[] = data?.answers ? [...data.answers] : []
    if (sortMode === 'wrongFirst') {
      base.sort((a,b)=>{
        const ac = !!a.effectiveCorrect, bc = !!b.effectiveCorrect
        if (ac === bc) return 0
        return ac ? 1 : -1 // błędne (false) na górze
      })
    }
    return base
  }, [data?.answers, sortMode])

  // --- RENDER ---
  if (!data) return <Wrap><p>{msg || 'Wczytywanie...'}</p><style>{css}</style></Wrap>

  return (
    <Wrap>
      <div className="top">
        <h2>Podsumowanie egzaminu</h2>
        <span className={`pill ${pass ? 'ok':'bad'}`}>{pass ? 'ZDANE' : 'NIEZDANE'}</span>
      </div>

      <div className="card meta">
        <div className="grid2">
          <div><label>Egzamin</label><div>{data.exam}</div></div>
          <div><label>Wynik</label><div><b>{data.result.good}/{data.result.total}</b> ({data.result.percent}%)</div></div>
          <div><label>Zdający</label><div>{(data.candidateTag || data.candidateNick) ? <span>{data.candidateTag || data.candidateNick}</span> : <span className="muted">brak</span>} <code>{data.discordId}</code></div></div>
          <div><label>Kanał</label><div><code>{data.channelId}</code></div></div>
          <div><label>Egzaminator</label><div>{(data.examinerNick || data.examinerId) ? <span>{data.examinerNick || ''}</span> : <span className="muted">brak</span>} {data.examinerId && <code>{data.examinerId}</code>}</div></div>
          <div><label>Średni czas</label><div>{avgSec!=null ? `${avgSec}s` : '-'}</div></div>
          <div><label>Start</label><div><small>{new Date(data.startedAt || data.createdAt).toLocaleString('pl-PL')}</small></div></div>
          <div><label>Koniec</label><div><small>{data.finishedAt ? new Date(data.finishedAt).toLocaleString('pl-PL') : '-'}</small></div></div>
        </div>

        <div className="toolbar">
          <label className="muted">Sortowanie:</label>
          <select value={sortMode} onChange={e=>setSortMode(e.target.value as any)}>
            <option value="default">domyślne</option>
            <option value="wrongFirst">błędne u góry</option>
          </select>
          {canModerate && (
            <span className="muted" style={{marginLeft:12}}>
              Zatwierdzone: <b>{data.result?.approvedCount || 0}</b>
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <div className="tableWrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Zdjęcie</th>
                <th>Odpowiedź</th>
                <th>Poprawność</th>
                <th>Czas</th>
                <th>Wklejone?</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((a:Answer, i:number)=>{
                const isApproved = !!(a.manual && (a.manual as any).by)
                const canApproveThis = canModerate && !a.correct // zatwierdzamy tylko pierwotnie błędne
                const originalIndex = (data.answers || []).findIndex(x => x.id === a.id)
                const rowIndex = originalIndex >= 0 ? originalIndex + 1 : i + 1
                return (
                  <tr key={a.id || i}>
                    <td>{rowIndex}</td>
                    <td><img src={`/assets/${data.exam}/${a.id}.${ext}`} alt="" style={{cursor:'pointer'}} onClick={()=>setSelectedImageId(a.id)} /></td>
                    <td>
                      {String(a.given||'')}
                      {isApproved && <span className="tag okTag" title={`Zatwierdził: ${(a.manual as any).tag || (a.manual as any).by}`}>zatwierdzone</span>}
                    </td>
                    <td>{a.effectiveCorrect ? '✅' : '❌'}</td>
                    <td>{a.timeMs ? `${a.timeMs} ms` : '-'}</td>
                    <td>{a.pasted ? 'TAK' : '-'}</td>
                    <td>
                      {canApproveThis ? (
                        isApproved
                          ? <button className="btn ghost" disabled={!!pending[a.id]} onClick={()=>toggleApprove(a.id, false)}>Cofnij</button>
                          : <button className="btn" disabled={!!pending[a.id]} onClick={()=>toggleApprove(a.id, true)}>Zatwierdź</button>
                      ) : <span className="muted">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedImageId && (
        <div className="modal-overlay" onClick={() => setSelectedImageId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImageId(null)}>✕</button>
            <img src={`/assets/${data.exam}/${selectedImageId}.${ext}`} alt="" className="modal-image" />
          </div>
        </div>
      )}

      {canModerate && (
        <div className="card" style={{marginTop:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <h3 style={{margin:0,fontSize:18}}>Dziennik zdarzeń</h3>
            <button className="btn ghost" onClick={loadAudit} disabled={loadingAudit}>{loadingAudit ? 'Ładowanie…' : 'Odśwież'}</button>
          </div>
          {audit.length === 0 ? (
            <p className="muted">{loadingAudit ? 'Ładowanie…' : 'Brak zdarzeń'}</p>
          ) : (
            <table className="tbl">
              <thead><tr><th>Czas</th><th>Użytkownik</th><th>Zdarzenie</th></tr></thead>
              <tbody>
                {audit.map((ev:any, i:number)=>(
                  <tr key={i}>
                    <td><small>{new Date(ev.at).toLocaleString('pl-PL')}</small></td>
                    <td><code>{ev.byId}</code>{ev.byTag ? ` (${ev.byTag})` : ''}</td>
                    <td>{ev.type==='approved' ? `Autoryzował odpowiedź ${ev.answerId}` : `Cofnął autoryzację ${ev.answerId}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <style>{css}</style>
    </Wrap>
  )
}

function Wrap({children}:{children:React.ReactNode}) {
  return (
    <div className="wrap">
      {children}
    </div>
  )
}

const css = `
:root{--bg:#0b0d12;--card:#111525;--border:#1f2430;--text:#e8e8ec;--muted:#a6a8ad;--ok:#2e9c4b;--bad:#ff6b6b}
*{box-sizing:border-box} body{margin:0;background:linear-gradient(180deg,#0b0d12,#0a0c14);color:var(--text);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial}
.wrap{max-width:1000px;margin:0 auto;padding:24px}
.muted{color:var(--muted)}
.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.pill{padding:6px 10px;border-radius:999px;background:#2a334a}
.pill.ok{background:#213527}
.pill.bad{background:#3a2020}

.card{background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);border:1px solid var(--border);border-radius:16px;padding:18px;box-shadow:0 12px 30px rgba(0,0,0,.35);margin-bottom:16px}
.meta .grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.meta label{display:block;color:var(--muted);font-size:12px;margin-bottom:4px}
.meta code{background:#10162a;border:1px solid #1b2542;border-radius:6px;padding:2px 6px}

.toolbar{display:flex;align-items:center;gap:8px;margin-top:12px}
select{background:#0e1220;border:1px solid #2b3242;color:#fff;border-radius:8px;padding:8px}

.tableWrap{overflow:auto}
.tbl{width:100%;border-collapse:collapse;min-width:760px}
.tbl th,.tbl td{border-bottom:1px solid #22283a;padding:10px;text-align:left;font-size:14px;vertical-align:top}
.tbl thead th{position:sticky;top:0;background:#0f1422;z-index:1;color:#a6a8ad}
.tbl img{max-width:180px;border-radius:10px;border:1px solid #272c39}

.btn{padding:8px 12px;border-radius:10px;border:0;background:#7a2cff;color:#fff;cursor:pointer}
.btn.ghost{background:#1b2440;border:1px solid #2a3046}
.btn[disabled]{opacity:.6;cursor:not-allowed}

.tag{display:inline-block;margin-left:8px;padding:2px 8px;border-radius:999px;font-size:12px;border:1px solid #2b3242;background:#10162a;color:#a6f0b0}
.okTag{border-color:#284a32;background:#0f1f14;color:#b8f3c4}

.modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.85);display:flex;align-items:center;justify-content:center;z-index:1000}
.modal-content{position:relative;max-width:90%;max-height:90%;background:#111525;border:1px solid #1f2430;border-radius:12px;padding:0;display:flex;align-items:center;justify-content:center}
.modal-image{max-width:100%;max-height:85vh;border-radius:10px}
.modal-close{position:absolute;top:12px;right:12px;background:#7a2cff;color:#fff;border:0;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:20px;z-index:1001}
.modal-close:hover{background:#9450ff}
`
